import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { List, ListItem, Typography } from "@mui/material";
import { monitorReason } from "../utils/monitorSnapshotState";
import Collapse from "@mui/material/Collapse";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { MonitorButton } from "./MonitorControls";
import { hasRecentErrorRead } from "../utils/errorMonitorState";
import "./ErrorMonitorNotice.css";

// Receives snapshot._errorMonitor. Dashboard owns loading and refresh controls.
export default function ErrorMonitorNotice({ monitor, stale = false, now = Date.now() }) {
  const { t, i18n } = useTranslation("errorMonitorCosts");
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  if (!monitor?.enabled) return null;
  const status = ["active", "budget_paused", "unavailable"].includes(monitor.status) ? monitor.status : "unknown";
  const locale = i18n.language?.startsWith("en") ? "en-GB" : "de-DE";
  const count = Number.isSafeInteger(monitor.loadedRecords) && monitor.loadedRecords >= 0
    ? monitor.loadedRecords.toLocaleString(locale) : t("message.unknown");
  // Only translate known reasons here; never expose arbitrary server text or cost data.
  const knownReason = typeof monitor.reason === "string" && monitor.reason !== "other" && /^[a-z][a-z0-9_]{0,95}$/i.test(monitor.reason)
    ? t(`notice.reason.${monitor.reason}`, { defaultValue: t(`reason.${monitor.reason}`, { defaultValue: t("reason.unknown") }) }) : t("reason.unknown");
  const gaps = Array.isArray(monitor.gaps) ? monitor.gaps : Array.isArray(monitor.coverage?.gaps) ? monitor.coverage.gaps : [];
  const gapSummary = monitor.gapSummary || monitor.coverage?.gapSummary;
  const repair = monitor.repair || monitor.coverage?.repair;
  const hasGaps = gaps.length > 0 || gapSummary?.count > 0;
  const limited = status === "unavailable" && hasRecentErrorRead(monitor, now);
  const state = stale ? "refreshFailed" : limited ? "limited" : status !== "active" ? status
    : (monitor.historyComplete !== true || hasGaps) ? "partial" : "active";
  const formatTime = value => {
    const time = typeof value === "number" ? value : typeof value === "string" ? Date.parse(value) : NaN;
    return Number.isFinite(time) && time > 0
      ? new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "medium" }).format(time) : t("message.unknown");
  };
  return <aside className={`error-monitor-notice error-monitor-notice--${state}`} aria-label={t("notice.label")}>
    <div className="error-monitor-notice-summary">
      <span role="status" aria-atomic="true">{t(`notice.summary.${state}`)}{status !== "active" && <> {t("pause.reason", { reason: knownReason })}</>}</span>
      <MonitorButton className="error-monitor-notice-toggle" aria-expanded={expanded} aria-controls={detailsId}
        onClick={() => setExpanded(value => !value)} endIcon={<ExpandMoreRounded aria-hidden="true"/>}>
        {t("notice.details")}
      </MonitorButton>
    </div>
    <Collapse in={expanded} timeout={0}>
      <div id={detailsId} className="error-monitor-notice-details" hidden={!expanded}>
        {stale && <p>{t("notice.refreshFailed")}</p>}
        {status !== "active" && <p>{t(`notice.${limited ? "limited" : status}`)} {t("pause.reason", { reason: knownReason })}</p>}
        {status === "budget_paused" && <p>{t("notice.stale")}</p>}
        <p>{t("notice.scope")}</p>
        {monitor.historyComplete !== true && <p>{t(monitor.historyComplete === false ? "notice.incomplete" : "notice.unknownHistory")}</p>}
        <p>{t("notice.observedAt", { time: formatTime(monitor.observedAt) })}</p>
        <p>{t("notice.checkedAt", { time: formatTime(monitor.checkedAt) })}</p>
        {hasGaps && <>
          <Typography component="p" variant="body2">{t("notice.gaps", { count: gaps.length })}</Typography>
          <List dense disablePadding aria-label={t("notice.gapList")}>
            {gaps.slice(0, 5).map((gap, index) => <ListItem key={`${gap.from}-${gap.until}-${index}`} disableGutters>
              <Typography component="span" variant="body2">{t("notice.gap", { from: formatTime(gap.from), until: formatTime(gap.until),
                reason: monitorReason({ reason: gap.reason }, t) })}</Typography>
            </ListItem>)}
          </List>
          {gaps.length > 5 && <p>{t("notice.moreGaps", { count: gaps.length - 5 })}</p>}
          {gapSummary?.count > 0 && <p>{t("notice.gapSummary", { count: gapSummary.count, from: formatTime(gapSummary.from), until: formatTime(gapSummary.until) })}</p>}
        </>}
        {repair && <p>{t("notice.repair", { from: formatTime(repair.from), until: formatTime(repair.until), checked: formatTime(repair.checkedAt) })}</p>}
        {monitor.nextRetryAt && <p>{t("notice.nextRetry", { time: formatTime(monitor.nextRetryAt) })}</p>}
        <p className="error-monitor-notice-count">{t("notice.loaded", { countText: count })}</p>
      </div>
    </Collapse>
  </aside>;
}
